import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import {
  ParkingMainRateOption,
  ParkingRateDto,
  ParkingRateMaster,
  TaiwanCarCategoryOption,
  TaiwanDiscountOption,
} from '../../../../../core/models/system-settings/parking-master-data.model';
import {
  PssSearchablePickerComponent,
  SearchablePickerOption,
} from '../../../../../tools/template/pss/pss-searchable-picker/pss-searchable-picker.component';

/** ช่องเลือกกฎ Taiwan: discount/fine × ประเภทรถ 1/2 (ไม่แยกประเภท ใช้เฉพาะช่อง 1) */
type RuleSlot = 'discount1' | 'fine1' | 'discount2' | 'fine2';

const RULE_SLOT_FIELD: Record<RuleSlot, 'taiwanDiscountId' | 'taiwanFineId' | 'taiwanDiscountId2' | 'taiwanFineId2'> = {
  discount1: 'taiwanDiscountId',
  fine1: 'taiwanFineId',
  discount2: 'taiwanDiscountId2',
  fine2: 'taiwanFineId2',
};

export interface ApplyPreviewRow {
  label: string;
  discountName: string | null;
  fineName: string | null;
}
import { MapperParkingMasterDataService } from '../../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { ApiParkingMasterDataService } from '../../../../../core/services/api/system-settings/api-parking-master-data.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-parking-rate',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent, PssSearchablePickerComponent],
  templateUrl: './modal-parking-rate.component.html',
  styleUrl: './modal-parking-rate.component.css',
})
export class ModalParkingRateComponent implements OnInit {
  @Input() itemId = 0;
  @Input() siteId = 0;
  @Input() siteName = '';
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;
  formData!: ParkingRateMaster;
  discountOptions: TaiwanDiscountOption[] = [];
  mainRateOptions: ParkingMainRateOption[] = [];
  isLoading = signal(false);
  isSaving = signal(false);

  private readonly api = inject(ApiParkingMasterDataService);
  private readonly mapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isEdit(): boolean { return this.itemId > 0; }

  /** true เมื่อค่าที่บันทึกไว้ไม่อยู่ในรายการ Main Rate ปัจจุบัน */
  get hasOrphanRateId(): boolean {
    const rateId = this.formData?.rateId?.trim();
    if (!rateId || this.mainRateOptions.length === 0) return false;
    return !this.mainRateOptions.some((o) => o.mainRateId === rateId);
  }

  ngOnInit(): void {
    this.formData = this.mapper.createEmptyRateMaster(this.siteId || null, this.siteName);
    const companyId = Number(this.cookieService.get('company_id') || 1);
    this.formData.companyId = companyId > 0 ? companyId : 1;
    this.loadMainRateOptions();
    this.loadDiscountOptions();
    this.loadCarCategoryOptions();
    if (this.isEdit) this.loadDetail();
  }

  onClose(): void { this.close.emit(); }

  onSave(): void {
    if (!this.formData.rateId?.trim()) {
      this.toast.error('กรุณาเลือกรหัสอัตราค่าบริการ (Main Rate)');
      return;
    }
    if (
      this.mainRateOptions.length > 0 &&
      !this.mainRateOptions.some((o) => o.mainRateId === this.formData.rateId.trim())
    ) {
      this.toast.error('rate_id ต้องเลือกจาก Main Rate ที่มีอยู่');
      return;
    }
    if (!this.formData.name?.trim()) {
      this.toast.error('กรุณาระบุชื่อ');
      return;
    }
    if (!this.formData.siteId) {
      this.toast.error('กรุณาเลือกลาน');
      return;
    }
    const splitError = this.validateSplit();
    if (splitError) {
      this.toast.error(splitError);
      return;
    }

    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลสิทธิ์ผู้ใช้งาน');
      return;
    }

    const payload = this.mapper.mapRateMasterPayload(this.formData, userId, menuId);
    const request$ = this.isEdit
      ? this.api.updateParkingRateMaster(this.itemId, payload)
      : this.api.insertParkingRateMaster(payload);

    this.isSaving.set(true);
    request$.subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        this.isSaving.set(false);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกสำเร็จ');
          this.saveSuccess.emit();
          this.onClose();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
      },
    });
  }

  private loadMainRateOptions(): void {
    this.api.getParkingMainRateOptions().subscribe({
      next: (res) => {
        this.mainRateOptions = this.mapper.mapMainRateOptions(res);
      },
      error: () => {
        this.mainRateOptions = [];
        this.toast.error('โหลดรายการ Main Rate ไม่สำเร็จ');
      },
    });
  }

  private loadDiscountOptions(): void {
    this.api.getTaiwanDiscountOptions().subscribe({
      next: (res) => {
        this.discountOptions = this.mapper.mapTaiwanDiscountOptions(res);
        this.refreshRuleOptions();
      },
      error: () => { this.discountOptions = []; },
    });
  }

  private loadDetail(): void {
    this.isLoading.set(true);
    this.api.getParkingRateMasterById(this.itemId).subscribe({
      next: (res) => {
        this.formData = this.mapper.mapRateMaster(res as ParkingRateDto);
        this.refreshRuleOptions();
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.toast.error('โหลดรายละเอียดไม่สำเร็จ');
        this.onClose();
      },
    });
  }

  get canShowActivityHistory(): boolean {
    return this.itemId > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }

  // ================= Taiwan rule by vehicle type =================

  carCategoryOptions: TaiwanCarCategoryOption[] = [];
  ruleOptions: Record<RuleSlot, SearchablePickerOption[]> = { discount1: [], fine1: [], discount2: [], fine2: [] };
  private ruleSearch: Record<RuleSlot, string> = { discount1: '', fine1: '', discount2: '', fine2: '' };

  categoryLabel(carCategory: number): string {
    if (carCategory === -1) return 'ทุกประเภทรถ';
    return this.carCategoryOptions.find((c) => c.carCategory === carCategory)?.label || `ประเภทรถ ${carCategory}`;
  }

  ruleValue(slot: RuleSlot): number | null {
    return this.formData?.[RULE_SLOT_FIELD[slot]] ?? null;
  }

  ruleName(slot: RuleSlot): string {
    const id = this.ruleValue(slot);
    if (!id) return '';
    const savedName: Record<RuleSlot, string> = {
      discount1: this.formData.taiwanDiscountName,
      fine1: this.formData.taiwanFineName,
      discount2: this.formData.taiwanDiscountName2,
      fine2: this.formData.taiwanFineName2,
    };
    return this.discountOptions.find((d) => d.id === id)?.name || savedName[slot] || `#${id}`;
  }

  onRuleSearch(slot: RuleSlot, text: string): void {
    this.ruleSearch[slot] = text;
    this.refreshRuleOptions();
  }

  onRuleSelected(slot: RuleSlot, option: SearchablePickerOption): void {
    this.formData[RULE_SLOT_FIELD[slot]] = option.id;
  }

  onRuleCleared(slot: RuleSlot): void {
    this.formData[RULE_SLOT_FIELD[slot]] = null;
    if (slot === 'discount1') this.formData.taiwanFineId = null;
    if (slot === 'discount2') this.formData.taiwanFineId2 = null;
  }

  /** เปิดแยกประเภท: ย้ายกฎเดิมที่เป็นของประเภท 2 ไปช่องประเภท 2 · ปิด: เหลือช่องเดียว */
  onSplitToggle(split: boolean): void {
    const f = this.formData;
    f.splitByVehicleType = split;
    if (split) {
      if (this.ruleCategory(f.taiwanDiscountId) === 2 && !f.taiwanDiscountId2) {
        f.taiwanDiscountId2 = f.taiwanDiscountId;
        f.taiwanDiscountId = null;
      }
      if (this.ruleCategory(f.taiwanFineId) === 2 && !f.taiwanFineId2) {
        f.taiwanFineId2 = f.taiwanFineId;
        f.taiwanFineId = null;
      }
    } else {
      if (!f.taiwanDiscountId && f.taiwanDiscountId2) {
        f.taiwanDiscountId = f.taiwanDiscountId2;
        f.taiwanFineId = f.taiwanFineId ?? f.taiwanFineId2;
      }
      f.taiwanDiscountId2 = null;
      f.taiwanFineId2 = null;
    }
    this.refreshRuleOptions();
  }

  /** สรุปว่าตอน Apply Stamp จะส่งกฎไหนไป Taiwan */
  get applyPreview(): ApplyPreviewRow[] {
    if (!this.formData) return [];
    if (!this.formData.splitByVehicleType) {
      return [{ label: 'รถทุกประเภท', discountName: this.ruleName('discount1') || null, fineName: this.ruleName('fine1') || null }];
    }
    return [
      { label: this.categoryLabel(1), discountName: this.ruleName('discount1') || null, fineName: this.ruleName('fine1') || null },
      { label: this.categoryLabel(2), discountName: this.ruleName('discount2') || null, fineName: this.ruleName('fine2') || null },
    ];
  }

  private loadCarCategoryOptions(): void {
    this.api.getTaiwanCarCategoryOptions().subscribe({
      next: (res) => { this.carCategoryOptions = this.mapper.mapTaiwanCarCategoryOptions(res); this.refreshRuleOptions(); },
      error: () => { this.carCategoryOptions = []; },
    });
  }

  private ruleCategory(id: number | null): number | null {
    if (!id) return null;
    return this.discountOptions.find((d) => d.id === id)?.carCategory ?? null;
  }

  private refreshRuleOptions(): void {
    const split = !!this.formData?.splitByVehicleType;
    (Object.keys(RULE_SLOT_FIELD) as RuleSlot[]).forEach((slot) => {
      const category = slot.endsWith('2') ? 2 : 1;
      const q = this.ruleSearch[slot].trim().toLowerCase();
      this.ruleOptions[slot] = this.discountOptions
        .filter((d) => !split || d.carCategory === category || d.carCategory === -1)
        .filter((d) => !q || d.name.toLowerCase().includes(q) || String(d.id) === q)
        .map((d) => ({ id: d.id, label: d.name, subLabel: `${this.categoryLabel(d.carCategory)} · id ${d.id}` }));
    });
  }

  private validateSplit(): string | null {
    const f = this.formData;
    if (!f.splitByVehicleType) return null;
    if (!f.taiwanDiscountId && !f.taiwanDiscountId2) return 'แยกประเภทรถแล้ว ต้องเลือกส่วนลด Taiwan อย่างน้อย 1 ประเภท';
    if (f.taiwanFineId && !f.taiwanDiscountId) return `${this.categoryLabel(1)}: มีค่าปรับแต่ยังไม่เลือกส่วนลด`;
    if (f.taiwanFineId2 && !f.taiwanDiscountId2) return `${this.categoryLabel(2)}: มีค่าปรับแต่ยังไม่เลือกส่วนลด`;
    return null;
  }
}
